import React, { FC } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { HistoricalOrderLine } from 'models/historical-order';
import { formatCurrency } from 'utils/format';
import InfoText from 'components/shared/info-text.component';

/**
 * DD-3687: an archived order's actions and materials, with the total the
 * source system stated.
 *
 * One shape for both kinds, because the archive stores them that way — the
 * type came from which file the row was imported from, not from a column.
 * Nothing is recalculated: the grand total is the figure the export carried,
 * not a sum of these rows, so a legacy order whose lines never quite added up
 * still shows what the old system showed.
 */

type HistoricalOrderLinesProps = {
    lines: HistoricalOrderLine[];
    totalInclVat?: number;
    currency?: string;
};

const HistoricalOrderLines: FC<HistoricalOrderLinesProps> = ({ lines, totalInclVat, currency }) => {
    const { t: translate } = useTranslation(['orders', 'common']);

    if (lines.length === 0) {
        return <InfoText text={translate('orders:HISTORICAL_NO_LINES')} />;
    }

    return (
        <TableContainer>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>{translate('orders:TYPE')}</TableCell>
                        <TableCell>{translate('orders:CODE')}</TableCell>
                        <TableCell>{translate('orders:DESCRIPTION')}</TableCell>
                        <TableCell align="right">{translate('orders:QUANTITY')}</TableCell>
                        <TableCell align="right">{translate('orders:UNIT_PRICE')}</TableCell>
                        <TableCell align="right">{translate('orders:VAT')}</TableCell>
                        <TableCell align="right">{translate('orders:TOTAL')}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {lines.map((line, lineIndex) => (
                        <TableRow key={`${line.code}-${lineIndex}`}>
                            <TableCell>{translate(`orders:HISTORICAL_LINE_${line.type.toUpperCase()}`)}</TableCell>
                            <TableCell>{line.code}</TableCell>
                            <TableCell>{line.description}</TableCell>
                            <TableCell align="right">{line.quantity}</TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                {formatCurrency(line.unitPrice ?? 0, currency)}
                            </TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                {formatCurrency(line.vatAmount ?? 0, currency)}
                            </TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                {formatCurrency(line.lineTotal ?? 0, currency)}
                            </TableCell>
                        </TableRow>
                    ))}
                    <TableRow sx={{ borderTop: '2px solid #bbb' }}>
                        <TableCell colSpan={6} align="right" sx={{ borderBottom: 0 }}>
                            <strong>{translate('orders:GRAND_TOTAL')}</strong>
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap', borderBottom: 0 }}>
                            <strong>{formatCurrency(totalInclVat ?? 0, currency)}</strong>
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default HistoricalOrderLines;
